import React, {FC, useCallback, useEffect, useState} from 'react'
import {Alert, Button, Form, Modal, Table} from 'react-bootstrap'
import Swal from 'sweetalert2'
import {salesforceApiService, SalesforceCatalogItem} from '../services/salesforceApi'

interface SalesforceCatalogModalProps {
  show: boolean
  onClose: () => void
}

const emptyForm = {
  id: null as number | null,
  type: '',
  service: '',
  subservice: '',
  service_product: '',
  sla_hours: '',
}

const swalThemeBackground = () =>
  (window.localStorage.getItem('kt_theme_mode_value') || '') === 'dark' ? '#353b48' : '#fff'

const SalesforceCatalogModal: FC<SalesforceCatalogModalProps> = ({show, onClose}) => {
  const [items, setItems] = useState<SalesforceCatalogItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [form, setForm] = useState(emptyForm)

  const loadItems = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const data = await salesforceApiService.getCatalog()
      setItems(data)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar catálogo')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (show) {
      loadItems()
      setForm(emptyForm)
    }
  }, [show, loadItems])

  const handleSave = async () => {
    const type = form.type.trim()
    const service = form.service.trim()
    const subservice = form.subservice.trim()
    const serviceProduct = form.service_product.trim()
    const slaHours = Number(form.sla_hours)

    if (!type || !service || !subservice || !slaHours || slaHours <= 0) {
      Swal.fire({
        title: 'Preencha todos os campos',
        text: 'Tipo, Serviço, Subserviço e SLA (horas maior que zero) são obrigatórios.',
        icon: 'warning',
        background: swalThemeBackground(),
      })
      return
    }

    try {
      setIsSaving(true)
      if (form.id) {
        await salesforceApiService.updateCatalogItem(form.id, {
          type,
          service,
          subservice,
          service_product: serviceProduct || null,
          sla_hours: slaHours,
        })
      } else {
        await salesforceApiService.createCatalogItem({
          type,
          service,
          subservice,
          service_product: serviceProduct || null,
          sla_hours: slaHours,
        })
      }
      setForm(emptyForm)
      await loadItems()
    } catch (err: unknown) {
      Swal.fire({
        title: 'Erro ao salvar',
        text: err instanceof Error ? err.message : 'Tente novamente.',
        icon: 'error',
        background: swalThemeBackground(),
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleEdit = (item: SalesforceCatalogItem) => {
    setForm({
      id: item.id,
      type: item.type,
      service: item.service,
      subservice: item.subservice,
      service_product: item.service_product ?? '',
      sla_hours: String(item.sla_hours),
    })
  }

  const handleDelete = async (item: SalesforceCatalogItem) => {
    const result = await Swal.fire({
      title: 'Remover item do catálogo?',
      text: `${item.type} / ${item.service} / ${item.subservice}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Remover',
      cancelButtonText: 'Cancelar',
      background: swalThemeBackground(),
    })

    if (!result.isConfirmed) {
      return
    }

    try {
      await salesforceApiService.deleteCatalogItem(item.id)
      await loadItems()
    } catch (err: unknown) {
      Swal.fire({
        title: 'Erro ao remover',
        text: err instanceof Error ? err.message : 'Tente novamente.',
        icon: 'error',
        background: swalThemeBackground(),
      })
    }
  }

  const handleClose = () => {
    setForm(emptyForm)
    onClose()
  }

  return (
    <Modal show={show} onHide={handleClose} size='lg' centered backdrop='static'>
      <Modal.Header closeButton>
        <Modal.Title>Catálogo de Produtos Salesforce</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className='text-muted fs-7'>
          Combinações de Tipo/Serviço/Subserviço e o SLA (em horas) usado para calcular
          automaticamente o prazo de entrega das tarefas importadas do Salesforce.
        </p>

        {error && <Alert variant='danger'>{error}</Alert>}

        <div className='row g-2 align-items-end mb-4'>
          <div className='col-md-2'>
            <Form.Label className='fs-8 fw-bold'>Tipo</Form.Label>
            <Form.Control
              value={form.type}
              onChange={(e) => setForm({...form, type: e.target.value})}
            />
          </div>
          <div className='col-md-2'>
            <Form.Label className='fs-8 fw-bold'>Serviço</Form.Label>
            <Form.Control
              value={form.service}
              onChange={(e) => setForm({...form, service: e.target.value})}
            />
          </div>
          <div className='col-md-2'>
            <Form.Label className='fs-8 fw-bold'>Subserviço</Form.Label>
            <Form.Control
              value={form.subservice}
              onChange={(e) => setForm({...form, subservice: e.target.value})}
            />
          </div>
          <div className='col-md-2'>
            <Form.Label className='fs-8 fw-bold'>Produto Atendimento</Form.Label>
            <Form.Control
              value={form.service_product}
              onChange={(e) => setForm({...form, service_product: e.target.value})}
            />
          </div>
          <div className='col-md-2'>
            <Form.Label className='fs-8 fw-bold'>SLA (horas)</Form.Label>
            <Form.Control
              type='number'
              min={1}
              value={form.sla_hours}
              onChange={(e) => setForm({...form, sla_hours: e.target.value})}
            />
          </div>
          <div className='col-md-2 d-flex gap-1'>
            <Button variant='primary' size='sm' onClick={handleSave} disabled={isSaving}>
              {form.id ? 'Salvar' : 'Add'}
            </Button>
          </div>
          {form.id && (
            <div className='col-12'>
              <Button variant='link' size='sm' className='p-0' onClick={() => setForm(emptyForm)}>
                Cancelar edição
              </Button>
            </div>
          )}
        </div>

        <div className='table-responsive' style={{maxHeight: 300, overflowY: 'auto'}}>
          <Table size='sm' hover>
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Serviço</th>
                <th>Subserviço</th>
                <th>Produto Atendimento</th>
                <th>SLA (h)</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={6} className='text-center text-muted'>
                    Carregando...
                  </td>
                </tr>
              )}
              {!isLoading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className='text-center text-muted'>
                    Nenhum item cadastrado
                  </td>
                </tr>
              )}
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.type}</td>
                  <td>{item.service}</td>
                  <td>{item.subservice}</td>
                  <td>{item.service_product ?? '-'}</td>
                  <td>{item.sla_hours}</td>
                  <td className='text-end'>
                    <Button variant='link' size='sm' className='p-0 me-3' onClick={() => handleEdit(item)}>
                      Editar
                    </Button>
                    <Button
                      variant='link'
                      size='sm'
                      className='p-0 text-danger'
                      onClick={() => handleDelete(item)}
                    >
                      Remover
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={handleClose}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default SalesforceCatalogModal
